import React from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Button,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  DismissRegular,
  ErrorCircleRegular,
  WarningRegular,
  ChevronRightRegular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
  drawer: {
    zIndex: 1000,
  },
  errorItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  warningItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  calculationMessageItem: {
    display: "flex",
    alignItems: "flex-start",
    padding: "12px 16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
    marginRight: "12px",
    flexShrink: 0,
  },
  warningIcon: {
    color: tokens.colorPaletteYellowForeground2,
    marginRight: "12px",
    flexShrink: 0,
  },
  errorContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  errorField: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  errorMessage: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase100,
  },
  errorTab: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  chevronIcon: {
    color: tokens.colorNeutralForeground3,
    marginLeft: "8px",
    flexShrink: 0,
  },
  header: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px",
    paddingRight: "8px", // Reduce right padding to move close button closer to edge
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingRight: "48px", // Add padding to prevent overlap with close button
  },
  headerIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  noErrors: {
    textAlign: "center",
    padding: "32px 16px",
    color: tokens.colorNeutralForeground2,
  },
  errorCount: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  closeButton: {
    position: "absolute",
    top: "8px",
    right: "8px",
    minWidth: "32px",
    minHeight: "32px",
    padding: "6px",
    border: "none",
    backgroundColor: "transparent",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
});

export interface ValidationError {
  field: string;
  fieldPath: string; // Original field path for DOM queries
  message: string;
  tab: string;
  tabIndex: number;
}

export interface CalculationMessage {
  level: string; // "1" for warnings, "2" for errors
  text: string;
  type: "calculation";
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  errors: ValidationError[];
  calculationMessages?: CalculationMessage[];
  onErrorClick: (error: ValidationError) => void;
}

const ValidationErrorSidebar: React.FC<Props> = ({
  isOpen,
  onClose,
  errors,
  calculationMessages = [],
  onErrorClick,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  // Separate calculation messages into errors and warnings
  const calculationErrors = calculationMessages.filter(
    (msg) => msg.level === "2"
  );
  const calculationWarnings = calculationMessages.filter(
    (msg) => msg.level === "1"
  );

  // Total count for header
  const totalCount =
    calculationErrors.length + calculationWarnings.length + errors.length;
  const hasAnyMessages = totalCount > 0;

  return (
    <Drawer
      type="overlay"
      separator
      open={isOpen}
      onOpenChange={(_, { open }) => !open && onClose()}
      position="end"
      size="medium"
      className={classes.drawer}
    >
      <DrawerHeader className={classes.header}>
        <div className={classes.headerTitle}>
          <ErrorCircleRegular className={classes.headerIcon} />
          <DrawerHeaderTitle>
            {calculationMessages.length > 0
              ? "Calculation Messages"
              : t("ila.validation.errorsTitle")}
            {totalCount > 0 && (
              <span className={classes.errorCount}>({totalCount})</span>
            )}
          </DrawerHeaderTitle>
        </div>
        <Button
          appearance="subtle"
          aria-label={t("ila.validation.close")}
          icon={<DismissRegular />}
          onClick={onClose}
          className={classes.closeButton}
        />
      </DrawerHeader>
      <DrawerBody>
        {!hasAnyMessages ? (
          <div className={classes.noErrors}>
            <Text>{t("ila.validation.noErrors")}</Text>
          </div>
        ) : (
          <div>
            {/* Calculation Messages Section (at the top) */}
            {calculationMessages.length > 0 && (
              <>
                {/* Calculation Errors First */}
                {calculationErrors.map((message, index) => (
                  <div
                    key={`calc-error-${index}`}
                    className={classes.calculationMessageItem}
                  >
                    <ErrorCircleRegular className={classes.errorIcon} />
                    <div className={classes.errorContent}>
                      <div className={classes.errorMessage}>{message.text}</div>
                      <div className={classes.errorTab}>Calculation Error</div>
                    </div>
                  </div>
                ))}

                {/* Calculation Warnings */}
                {calculationWarnings.map((message, index) => (
                  <div
                    key={`calc-warning-${index}`}
                    className={classes.calculationMessageItem}
                  >
                    <WarningRegular className={classes.warningIcon} />
                    <div className={classes.errorContent}>
                      <div className={classes.errorMessage}>{message.text}</div>
                      <div className={classes.errorTab}>
                        Calculation Warning
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Validation Errors Section */}
            {errors.length > 0 && (
              <>
                {calculationMessages.length > 0 && (
                  <Text
                    style={{
                      padding: "16px",
                      fontSize: tokens.fontSizeBase200,
                      color: tokens.colorNeutralForeground2,
                      display: "block",
                      fontWeight: tokens.fontWeightSemibold,
                      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    }}
                  >
                    Form Validation Errors
                  </Text>
                )}

                <Text
                  style={{
                    padding: "16px",
                    fontSize: tokens.fontSizeBase200,
                    color: tokens.colorNeutralForeground2,
                    display: "block",
                  }}
                >
                  {t("ila.validation.clickToNavigate")}
                </Text>

                {errors.map((error, index) => (
                  <button
                    key={`${error.field}-${index}`}
                    className={classes.errorItem}
                    onClick={() => onErrorClick(error)}
                  >
                    <ErrorCircleRegular className={classes.errorIcon} />
                    <div className={classes.errorContent}>
                      <div className={classes.errorField}>
                        {t(error.field, error.field)}
                      </div>
                      <div className={classes.errorMessage}>
                        {error.message}
                      </div>
                      <div className={classes.errorTab}>{error.tab}</div>
                    </div>
                    <ChevronRightRegular className={classes.chevronIcon} />
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </DrawerBody>
    </Drawer>
  );
};

export default ValidationErrorSidebar;
